import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  GitBranch,
  Building2,
  Users,
  FlaskConical,
  Blocks,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import logoSvg from '../../assets/logo.svg';

type NavItem =
  | { name: string; to: string; icon: LucideIcon; end?: boolean; children?: never }
  | { name: string; to?: never; icon?: never; end?: never; children: { name: string; to: string; icon: LucideIcon }[] };

const navigation: NavItem[] = [
  { name: 'Dashboard', to: '/', icon: LayoutDashboard, end: true },
  { name: 'Orders', to: '/orders', icon: ShoppingCart },
  { name: 'Products', to: '/products', icon: Package },
  { name: 'Lenders', to: '/lenders', icon: Building2 },
  { name: 'Users', to: '/users', icon: Users },
  {
    name: 'Rules',
    children: [
      { name: 'Product Rules', to: '/rules/product', icon: Blocks },
      { name: 'Order Logic', to: '/rules/order-logic', icon: GitBranch },
      { name: 'Simulator', to: '/rules/simulator', icon: FlaskConical },
    ],
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function Sidebar({ open, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r transition-all duration-200 lg:static lg:z-auto',
          'bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700',
          collapsed ? 'lg:w-16' : 'lg:w-60',
          // Mobile: always full width, slide in/out
          open ? 'translate-x-0 w-60' : '-translate-x-full w-60',
          'lg:translate-x-0',
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-14 border-b border-gray-200 dark:border-gray-700 px-2">
          <div className="flex-1 flex items-center justify-center">
            <img
              src={logoSvg}
              alt="FirstClose"
              className={collapsed ? 'h-5 w-auto' : 'h-6 w-auto'}
              style={{ filter: 'var(--logo-filter, none)' }}
            />
          </div>
          <button onClick={onClose} className="btn-icon lg:hidden shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={clsx('flex-1 overflow-y-auto py-2', collapsed ? 'px-1.5' : 'px-2')}>
          <div className="space-y-0.5">
            {navigation.map((item) => {
              if (item.children) {
                return (
                  <div key={item.name}>
                    {!collapsed && (
                      <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-3">
                        {item.name}
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={onClose}
                            title={collapsed ? child.name : undefined}
                            className={({ isActive }) =>
                              clsx(
                                'flex items-center rounded-lg text-sm font-medium transition-colors',
                                collapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-1.5',
                                isActive
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
                              )
                            }
                          >
                            <ChildIcon className="h-4 w-4 shrink-0" />
                            {!collapsed && <span className="truncate">{child.name}</span>}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const ItemIcon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  title={collapsed ? item.name : undefined}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center rounded-lg text-sm font-medium transition-colors',
                      collapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-1.5',
                      isActive
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
                    )
                  }
                >
                  <ItemIcon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:flex items-center border-t border-gray-200 dark:border-gray-700 p-2">
          <button
            onClick={onToggleCollapse}
            className={clsx(
              'btn-icon w-full',
              collapsed ? 'justify-center' : 'justify-start gap-2 px-3',
            )}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
